import Query from '@arcgis/core/rest/support/Query';
import { CellContext, createColumnHelper, PaginationState, Row } from '@tanstack/react-table';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Table from '../../../../components/shared/table/table';
import TableActions from '../../../../components/shared/table/tableActions/tableActions';
import { ColumnConfig, ColumnType, TableProps } from '../../../../components/shared/table/types/tableProps';
import { apiContext } from '../../../../contexts/apiContext';
import { HeadersTable } from '../../../../helpers/featureTableConstruct';
import { tableFeatureService } from '../../../../services/arcGisService/arcGisService';
import { NotificationViewProps } from './types/notificationViewProps';
import { readNotification } from '../../../../services/communicationService/communicationService';
import { notifyError } from '../../../../components/snackbar/snackbar';
import AuthContext, { NotificationFrequency } from '../../../../contexts/authContext';
import './notificationView.scss';
import { getTableSettings, setTableSettings } from '../../../../database/indexedDbReadWrite';

const DATABASE_TABLE = 'NotifyT';

const NotificationView = (props: NotificationViewProps) => {
    const api = useContext(apiContext);
    const { userConfig, notificationCount, setNotificationCount, username } = useContext(AuthContext);
    const [loading, setLoading] = useState<boolean>(true);
    const [featureSetExtend, setFeatureSetExtend] = useState<__esri.Graphic[]>([]);
    const [rowsToFoldOut, setRowsToFoldOut] = useState<string[]>([]);
    const [reload, setReload] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 0 });
    const [pageCount, setPageCount] = useState<number>(1);
    const [allCount, setAllCount] = useState<number>(0);
    const [headers, setHeaders] = useState<HeadersTable>({});
    const columnConfig = useRef<ColumnConfig[]>([{name: "DATA_WYSLANIA", type: ColumnType.Date}]);

    useEffect(() => {
        if (pagination.pageSize === 0) {
            getTableSettings(username, DATABASE_TABLE).then((tSettings) => {
                setPagination({ pageIndex: 0, pageSize: tSettings.pageSize });
            }, () => {
                setPagination({ pageIndex: 0, pageSize: 10 })
            });
        }
    }, [username, pagination.pageSize])

    const recalcPagination = useCallback((all: number) => {
        const newPageCount = all !== undefined ? Math.ceil(all / pagination.pageSize) : 1;
        if (newPageCount < pagination.pageIndex + 1) {
            const newPageIndex = newPageCount - 1 >= 0 ? newPageCount - 1 : 0;
            setPagination({ pageSize: pagination.pageSize, pageIndex: newPageIndex });
        }
        setPageCount(newPageCount);
        setAllCount(all);
    }, [pagination.pageIndex, pagination.pageSize])

    const onPaginationChange = (name: string, value: any) => {
        if (name === 'pageSize') {
            setTableSettings(username, {name: DATABASE_TABLE, pageSize: value});
        }
    }

    const getInterval = (frequency: NotificationFrequency) => {
        switch (frequency) {
            case NotificationFrequency.Every5min:
                return 300000;
            case NotificationFrequency.Every15min:
                return 900000;
            case NotificationFrequency.EveryHour:
                return 3600000;
            default:
                return 3600000;
        }
    }

    const reloadTable = useCallback(() => {
        setReload(!reload);
    }, [reload])

    useEffect(() => {
        const interval = userConfig.notificationFrequency;
        const intervalTime = getInterval(interval);
        const intervalId = setInterval(reloadTable, intervalTime);
        return () => {
            clearInterval(intervalId);
        }
    }, [props.where, reloadTable, userConfig.notificationFrequency]);

    useEffect(() => {
        if (pagination.pageSize === 0) {
            return;
        }
        const query = new Query({
            outFields: ["*"],
            returnGeometry: false,
            start: pagination.pageIndex*pagination.pageSize,
            num: pagination.pageSize,
            orderByFields: ['DATA_WYSLANIA DESC'],
            where: props.where ? props.where : "0=1"
        })
        setLoading(true);
        let _query = tableFeatureService({ url: api.featureServices.notifications }, query);
        _query.promise.then((res: any) => {
            prepareData(res.featureSet.data);
            setFeatureSetExtend(res.featureSet.data);

            let tableHeaders = {
                KATEGORIA_DOM_WARTOSC: "Kategoria",
                DATA_WYSLANIA: "Data",
                shortContent: "Treść skrócona",
            };
            setHeaders(tableHeaders);
            recalcPagination(res.count);
            setLoading(false);
        }).catch((err: any) => {
            if (err?.reason !== "cancel") {
                setLoading(false);
            }
        })
        return () => {
            _query.cancel();
        }
    }, [api.featureServices.notifications, pagination.pageIndex, pagination.pageSize, props.where, recalcPagination, reload]);

    const prepareData = (features: __esri.Graphic[]) => {
        features.forEach(feature => {
            feature.attributes.TRESC = feature.attributes.TRESC?.replaceAll('\\n', '\n');
            if (feature.attributes.TRESC.length > 200) {
                feature.attributes.shortContent = feature.attributes.TRESC.substring(0, 200) + "...";
            } else {
                feature.attributes.shortContent = feature.attributes.TRESC;
            }
        });
    }

    const cellAction = (properties: CellContext<any, unknown>) => {
        return (<TableActions
            row={properties.row}
            objectType={"notification"}
            readClickHandler={handleReadClick}
        />)
    }

    const handleReadClick = (row: any, isRead: boolean) => {
        readClick(row.original, isRead);
    }

    const readClick = (feature: __esri.Graphic, isRead: boolean) => {
        const id = feature.attributes.POWIADOMIENIA_OSOBY_ID;
        const url = api.apiUrl + api.apiVersion;
        setLoading(true);
        readNotification(url, id, isRead).then((isSuccess: boolean) => {
            if (isSuccess) {
                var newData = featureSetExtend.map(f => {
                    if (f.attributes.POWIADOMIENIA_OSOBY_ID === id) {
                        f.attributes.CZY_ODCZYTANE = isRead ? 1 : 0;
                    };
                    return f;
                })
                setFeatureSetExtend(newData);
                const count = isRead ? notificationCount - 1 : notificationCount + 1;
                setNotificationCount(count > 0 ? count : 0);
            } else {
                showStatusChangeFailed();
            }
            setLoading(false);
        }).catch(err => {
            console.error(err);
            showStatusChangeFailed();
            setLoading(false);
        });
    }

    const showStatusChangeFailed = () => {
        notifyError({ message: "Nie udało się zaktualizować statusu powiadomienia" });
    }

    const isHighlight = (row: Row<any>) => {
        return !row.original.attributes.CZY_ODCZYTANE;
    }

    const handleTrClick = (features: __esri.Graphic[]) => {
        const feature = features[0];
        if (!feature.attributes.CZY_ODCZYTANE) {
            readClick(feature, true);
        }
        const featureId = feature.attributes.POWIADOMIENIA_OSOBY_ID;
        if (rowsToFoldOut.includes(featureId)) {
            setRowsToFoldOut([...rowsToFoldOut.filter((item: any) => {
                return item !== featureId;
            })]); 
        } else {
            setRowsToFoldOut([...rowsToFoldOut, featureId]); 
        }
    }

    const prepareFoldoutContent = (row: any) => {
        return <div className="notification-foldout">
            {row.attributes.TRESC}
        </div>
    }

    const foldoutParamToCheck = (row: any) => {
        return row.attributes.POWIADOMIENIA_OSOBY_ID;
    }

    let cellWidth = {
        DATA_WYSLANIA: '110px'
    }

    var tableProps: TableProps = {
        columnAliases: headers,
        columnHelper: createColumnHelper<HeadersTable>(),
        columnNames: Object.keys(headers),
        columnConfig: columnConfig.current,
        cellWidth: cellWidth,
        cellActions: cellAction,
        highlightFunc: isHighlight,
        onTrClick: handleTrClick,
        loading: loading,
        data: featureSetExtend,
        tableFiltersVisible: false,
        stopSelection: true,
        isPagination: true,
        allCount: allCount,
        pagination: pagination,
        setPagination: setPagination,
        pageCount: pageCount,
        setPageCount: setPageCount,
        onPaginationChange: onPaginationChange,
        foldoutRows: true,
        rowsToFoldOut: rowsToFoldOut,
        isTrClickable: true,
        foldoutContent: prepareFoldoutContent,
        foldoutParamToCheck: foldoutParamToCheck
    }

    return (<>
        <div className="notificationView">
            {pagination.pageSize !== 0 && <Table {...tableProps} />}
        </div>
    </>)
}

export default NotificationView;